import { createContext, useContext, useEffect, useState, type ReactNode, useCallback } from "react";
import { wishlistApi, type WishlistItemResponse } from "@/lib/api";
import { useAuth } from "./AuthContext";
import { toast } from "sonner";

/* ── Favorites / Wishlist Context ───────────────────────────────
   BACKEND ENDPOINTS (see api.ts wishlistApi):
   GET    /api/v1/users/wishlist     → wishlistApi.list()     (fetch saved)
   POST   /api/v1/users/wishlist     → wishlistApi.add()      (save venue)
   DELETE /api/v1/users/wishlist/:id → wishlistApi.remove()   (unsave venue)
   ─────────────────────────────────────────────────────────────── */

interface Ctx {
  favorites: string[];
  isFavorite: (id: string) => boolean;
  toggle: (id: string) => Promise<void>;
  loading: boolean;
}

const FavCtx = createContext<Ctx | null>(null);

export function FavoritesProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [items, setItems] = useState<WishlistItemResponse[]>([]);
  const [loading, setLoading] = useState(true);

  /* fetch on mount / auth change */
  useEffect(() => {
    if (!isAuthenticated) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    wishlistApi
      .list()
      .then((res) => setItems(res))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [isAuthenticated]);

  const toggle = useCallback(async (venueId: string) => {
    if (!isAuthenticated) {
      toast.error("Sign in to save favorites");
      return;
    }
    const existing = items.find((i) => i.venueId === venueId);
    if (existing) {
      /* remove */
      try {
        await wishlistApi.remove(existing.id);
        setItems((prev) => prev.filter((i) => i.id !== existing.id));
      } catch {
        toast.error("Failed to remove from wishlist");
      }
    } else {
      /* add */
      try {
        const newItem = await wishlistApi.add(venueId);
        setItems((prev) => [...prev, newItem]);
      } catch {
        toast.error("Failed to add to wishlist");
      }
    }
  }, [items, isAuthenticated]);

  const favorites = items.map((i) => i.venueId);

  return (
    <FavCtx.Provider
      value={{ favorites, isFavorite: (id) => favorites.includes(id), toggle, loading }}
    >
      {children}
    </FavCtx.Provider>
  );
}

export function useFavorites() {
  const v = useContext(FavCtx);
  if (!v) throw new Error("useFavorites must be used within FavoritesProvider");
  return v;
}
